import type { FC } from "react";
import { useCallback, useEffect } from "react";

import { Card } from "@jti/core-components/components/Card";
import { Form, FormField, useForm } from "@jti/ui";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { LoyaltyReferralCodeControls } from "./components";

export type LoyaltyReferralCodeViewProps = {
  referralCode?: string;
  isSuccess: boolean;
  isLoading: boolean;
  error: string | null;
  onSubmit: (code: string) => void | Promise<void>;
  onFocusInput: () => void;
  collapsed: boolean;
};

export const LoyaltyReferralCodeView: FC<LoyaltyReferralCodeViewProps> = ({
  referralCode,
  isSuccess,
  isLoading,
  error,
  onSubmit,
  onFocusInput,
  collapsed,
}) => {
  const { __ } = useTranslations();

  const form = useForm<{ code: string }>({ defaultValues: { code: referralCode ?? "" } });

  useEffect(() => {
    if (referralCode !== null && referralCode !== undefined && referralCode !== form.getValues("code")) {
      form.setValue("code", referralCode);
    }
  }, [referralCode, form]);

  const handleSubmit = useCallback(({ code }: { code: string }) => onSubmit(code), [onSubmit]);

  return (
    <Card
      variant="level-1-club"
      title={__("cart.loyalty.referral.title")}
      description={__("cart.loyalty.referral.subtitle")}
      data-testid="loyaltyReferralCode"
      isCollapsible
      defaultCollapsed={collapsed}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <FormField
          name="code"
          render={({ field }) => (
            <LoyaltyReferralCodeControls
              {...field}
              isSuccess={isSuccess}
              isError={Boolean(error)}
              errorText={error}
              disabled={isLoading || isSuccess}
              onFocus={onFocusInput}
            />
          )}
        />
      </Form>
    </Card>
  );
};
